(ns minorodata.app.utils
  (:require ["cronstrue" :as cronstrue]
            ["date-fns" :refer [differenceInMilliseconds format formatISO
                                isBefore isToday isYesterday]]
            ["date-fns-tz" :refer [toZonedTime]]
            ["humanize-duration" :as humanizeDuration]
            ["sql-formatter-plus" :as sql-formatter-plus]
            [cljs.reader]
            [clojure.set :as c.set]
            [clojure.string :as c.string]
            [clojure.walk :as walk]
            [edamame.core :refer [parse-string]]
            [goog.string :as g.string]
            [goog.string.format]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.app.semver :as app.semver]))

(defn remove-nils [m]
  (let [f (fn [x]
            (if (map? x)
              (let [kvs (filter (comp not nil? second) x)]
                (if (empty? kvs) nil (into {} kvs)))
              x))]
    (walk/postwalk f m)))

(def production-env-id
  "production")

(defn reduce-kvs
  "Like `reduce-kv` but takes a flat sequence of kv pairs."
  [rf init kvs]
  (transduce (partition-all 2)
             (completing (fn [acc [k v]]
                           (rf acc k v)))
             init
             kvs))


(defn assoc-some
  "Assocs each kv if its value is not nil."
  ([m k v]
   (if (nil? v)
     (if (nil? m) {} m)
     (assoc m k v)))

  ([m k v & kvs]
   (reduce-kvs
    (fn [m k v]
      (if (nil? v) m (assoc m k v)))
    (assoc-some m k v)
    kvs))

  ([m kvs]
   (reduce-kv
    (fn [m k v]
      (if (nil? v) m (assoc m k v)))
    (if (nil? m) {} m)
    kvs)))


(defn conj-when
  "Conjoins each val when x is truthy."
  ([] [])
  ([coll] coll)
  ([coll x val]
   (if x (conj coll val) coll))

  ([coll x val & more]
   (reduce-kvs
    (fn [acc x v]
      (if x (conj acc v) acc))
    (conj-when coll x val)
    more)))


(defn trim? [maybe-string]
  (if (string? maybe-string)
    (c.string/trim maybe-string)
    maybe-string))


(defn clean-up-query [query]
  (->> (for [[k v] query
             :let [value (trim? v)]
             :when (or (nil? value)
                       (and (or (string? value) (seq? value) (vector? value) (map? value))
                            (empty? value)))]
         k)
       (apply dissoc query)))


(defn clean-up-query-recursive [query]
  (walk/postwalk
   (fn [node]
     (if (map? node)
       (clean-up-query node)
       node))
   query))

(defn format-date
  ([d]
   (format-date d {}))
  ([d {:keys [as-iso? today yesterday date timezone-to seconds?]
       :or   {as-iso?   false
              seconds?  false}}]
   (let [today     (or today (str "'Today' 'at' HH:mm" (when seconds? ":ss")))
         yesterday (or yesterday (str "'Yesterday' 'at' HH:mm" (when seconds? ":ss")))
         date      (or date (str "d MMM yy 'at' HH:mm" (when seconds? ":ss")))]
     (try
       (when (some? d)
         (let [date-obj (js/Date. d)
               date-obj (if timezone-to
                          (toZonedTime date-obj timezone-to)
                          date-obj)]
           (if as-iso?
             (formatISO date-obj)
             (cond
               (isToday date-obj) (format date-obj today)
               (and yesterday (isYesterday date-obj)) (format date-obj yesterday)
               :else (format date-obj date)))))
       (catch js/Error e
         (js/console.log "Date: " d)
         (js/console.error e)
         d)))))


(defn format-full-date
  ([d]
   (format-full-date d {}))
  ([d {:keys [date time]
       :or   {date "dd/MM/yy"
              time "HH:mm:ss"}}]
   (when (some? d)
     (let [date-obj (js/Date. d)]
       (format date-obj (str date " 'at' " time))))))

(defn group-in-ranges [items]
  (reduce (fn [acc n]
            (let [last-range   (last acc)
                  other-ranges (vec (butlast acc))]
              (if (some? last-range)
                ;; if new item n is right after the hour
                ;; that last-range ends with
                (if (= 1 (- n (last last-range)))
                  (conj other-ranges (conj last-range n))
                  (conj acc [n]))
                (conj acc [n]))))
          []
          (sort (distinct items))))


;; As per https://en.wikipedia.org/wiki/ISO_week_date
(def days-of-the-week
  {1 "Monday"
   2 "Tuesday"
   3 "Wednesday"
   4 "Thursday"
   5 "Friday"
   6 "Saturday"
   7 "Sunday"})


(def days-of-the-week-short
  {0 "Sun"
   1 "Mon"
   2 "Tue"
   3 "Wed"
   4 "Thu"
   5 "Fri"
   6 "Sat"
   7 "Sun"})

(def days-of-the-week-short-uc
  {1 "MON"
   2 "TUE"
   3 "WED"
   4 "THU"
   5 "FRI"
   6 "SAT"
   7 "SUN"})

(def days-of-the-week-short-cron
  {0 "SUN"
   1 "MON"
   2 "TUE"
   3 "WED"
   4 "THU"
   5 "FRI"
   6 "SAT"})

(def days-of-the-week-short-uc-inverted
  (c.set/map-invert days-of-the-week-short-cron))


(def days-range-cron (range 0 7))
(def days-range (range 1 8))
(def weekdays-range (range 1 6))
(def hours-range (range 24))
(def minutes-range (range 0 60 5))
(def months-range (range 1 13))
(def days-of-month-range (range 1 32))


(defn fancy-join [col sep last-sep]
  (case (count col)
    0 ""
    1 (first col)
    2 (str (first col) last-sep (last col))
    (let [end       (last col)
          beginning (butlast col)]
      (str (clojure.string/join sep beginning)
           last-sep
           end))))


(defn format-time [hour minute]
  (g.string/format "%02d:%02d" hour minute))


(defn format-hour-range [hour-range minute]
  (let [first-hour  (first hour-range)
        last-hour   (last hour-range)
        total-hours (count hour-range)]
    (if (= 1 total-hours)
      (format-time first-hour minute)
      (g.string/format "%02d–%02d:%02d" first-hour last-hour minute))))


(defn format-hours-with-ranges [hours minute]
  (let [minute'     (if (and (some? minute) (number? minute))
                      minute
                      0)
        hour-ranges (group-in-ranges hours)
        ranges-fmt  (map #(format-hour-range % minute') hour-ranges)]
    (fancy-join ranges-fmt ", " " and ")))


(defn format-day-range [day-range]
  (let [first-day  (first day-range)
        last-day   (last day-range)
        total-days (count day-range)]
    (if (= 1 total-days)
      (get days-of-the-week first-day)
      (str (get days-of-the-week first-day)
           "–"
           (get days-of-the-week last-day)))))


(defn format-days-with-ranges [days]
  (if (= (set days-range)
         (set days))
    "Every day"
    (let [day-ranges (group-in-ranges days)
          ranges-fmt (map format-day-range day-ranges)]
      (fancy-join ranges-fmt ", " " and "))))


(defn in-seq?
  "true if a sequence contains element"
  [seq elem]
  (some #(= elem %) seq))


(defn remove-elem [coll elem]
  ;; TODO check coll type
  (vec (remove #(= elem %) coll)))


(defn drop-ns-in-keys [record]
  (walk/postwalk
   (fn [node]
     (if (map? node)
       (reduce-kv
        (fn [acc k v]
          (if (and (keyword? k) (not= k :db/id))
            (assoc acc (keyword (name k)) v)
            (assoc acc k v)))
        {}
        node)
       ;; return node untouched
       node))
   record))


(defn remove-at-index
  "remove elem in coll"
  [coll pos]
  (vec (concat (subvec coll 0 pos) (subvec coll (inc pos)))))

(def empty-cron "* * * * *")

(defn expand-ranges [coll]
  (into []
        (reduce (fn [acc v]
                  (if (re-matches #"^\d+$" v)
                    (concat acc [(js/parseInt v 10)])
                    (let [[start end] (mapv #(js/parseInt % 10)
                                            (c.string/split v #"-"))]
                      (concat acc (range start (inc end))))))
                []
                coll)))

(defn dow->number [v]
  (if (= ["*"] v)
    v
    (mapv (fn [day-or-range]
            (let [splited (c.string/split day-or-range #"-")]
              (if (= 1 (count splited))
                (str (get days-of-the-week-short-uc-inverted day-or-range day-or-range))
                (str (get days-of-the-week-short-uc-inverted (first splited) (first splited))
                     "-"
                     (get days-of-the-week-short-uc-inverted (last splited) (last splited))))))
          v)))

(defn cron->map [cron-string]
  (let [splited (->> (c.string/split cron-string #" ")
                     (mapv #(c.string/split % #","))
                     (zipmap [:minute :hour :dom :month :dow]))
        all-ints (update splited :dow dow->number)]
    (into {}
          (map (fn [[k v]]
                 [k (if (= ["*"] v) v (expand-ranges v))])
               all-ints))))

(defn cron->schedule [cron]
  (if (= cron empty-cron)
    {}
    (let [{:keys [dow hour minute]} (cron->map cron)]
      {:days   (if (= dow ["*"]) [] dow)
       :hours  (if (= hour ["*"]) [] hour)
       :minute (first minute)})))

(def field-names
  {:minute "minutes"
   :hours "hours"
   :days "days of week"})

(defn format-schedule [{:keys [cron empty-status empty-invalid? required-fields]
                        :or   {empty-status "Dependency based"
                               empty-invalid? true}
                        :as   params}]
  (let [{:keys [days hours minute] :as cron-fields}
        (if cron
          (cron->schedule cron)
          {:days   (:days params)
           :hours  (:hours params)
           :minute (:minute params)})
        empty-days?   (empty? days)
        empty-hours?  (empty? hours)
        empty-minute? (or (nil? minute) (and (string? minute) (empty? minute)))
        missing-fields (and (not-empty required-fields)
                            (filter #(let [v (get cron-fields %)]
                                       (#{[] "*" nil} v))
                                    required-fields))]
    (cond
      (or (and empty-invalid? (= cron empty-cron))
          (and empty-days? empty-hours? empty-minute?))
      empty-status

      (not-empty missing-fields)
      (str "Please select " (c.string/join ", " (mapv field-names missing-fields)))

      cron
      (.toString cronstrue cron #js {:use24HourTimeFormat true})

      :else (js/console.error "Invalid schedule" params))))

(defn extract-schedule [extract & [empty-status]]
  (let [cron (some-> (or (:schedule-spec extract) (:extract/schedule-spec extract)) :cron)
        empty-status (or empty-status "No schedule")]
    (format-schedule
     {:cron           cron
      :empty-invalid? true
      :empty-status   empty-status})))

(defn transform-schedule [transform & [empty-status]]
  (let [cron (some-> (or (:schedule-spec transform) (:transform/schedule-spec transform)) :cron)
        empty-status (or empty-status "No schedule")]
    (format-schedule
     {:cron           cron
      :empty-invalid? true
      :empty-status   empty-status})))

(defn or-star [v]
  (if (empty? v) ["*"] v))

(defn collape-ranges
  ([col] (collape-ranges col identity))
  ([coll process]
   (mapv (fn [arr]
           (if (= ["*"] coll)
             "*"
             (if (= 1 (count arr))
               (process (first arr))
               (str (process (first arr)) "-" (process (last arr))))))
         (group-in-ranges coll))))

(defn ->cron [entity]
  (let [{:keys [minute hour dom month dow schedule-spec]}
        (drop-ns-in-keys entity)]
    (or (:cron schedule-spec)
        (let [dow    (collape-ranges (map #(if (= % 7) 0 %) (or-star dow)))
              hour   (collape-ranges (or-star hour))
              minute (collape-ranges (or-star
                                      (if (or (not minute) (sequential? minute))
                                        minute [minute])))
              dom    (collape-ranges (or-star dom))
              month  (collape-ranges (or-star month))]
          (c.string/join
           " "
           [(c.string/join "," minute)
            (c.string/join "," hour)
            (c.string/join "," dom)
            (c.string/join "," month)
            (c.string/join "," dow)])))))

(defn update-cron-field [current-value value]
  (cond
    (= ["*"] current-value)
    [value]

    (some #(= value %) current-value)
    (let [new-value (filter #(not= value %) current-value)]
      (if (empty? new-value) ["*"] new-value))

    :else
    (->> value
         (conj current-value)
         sort
         (into []))))

(defn get-cron-field [cron field]
  (-> cron cron->map (get field)))

(defn update-cron [cron field new-value & [replace]]
  (let [cron-map (cron->map cron)]
    (->cron
     (update cron-map
             field
             #(if replace
                new-value
                (update-cron-field % new-value))))))

(defn in-cron? [cron field value]
  (let [cron-map (cron->map cron)]
    (some #(= value %) (get cron-map field))))

(defn cron-empty-field? [cron field]
  (let [cron-map (if (map? cron) cron (cron->map cron))]
    (= ["*"] (get cron-map field))))


(defn has-schedule-value [val]
  (or (and (vector? val)
           (seq val))
      (number? val)))


(def default-options
  {:language "sql"
   :indent   "  "})


(defn format-code
  "Formats SQL statement. Logs an error and returns supplied variable
  unchanged if it can't format the string for any reason"
  ([sql] (format-code sql {}))
  ([sql user-options]
   (try
     (assert (string? sql) (str "Expected a string, but got: " sql))

     (let [options (merge default-options user-options)]
       (sql-formatter-plus/format sql (clj->js options)))

     (catch js/Object e
       (js/console.warn e)
       sql))))


(def default-pagination
  {:items-per-page 50 :page 0})


(defn parse-token
  "Parses data from JWT token"
  [token]
  (when (some? token)
    (let [parts   (clojure.string/split token #"\.")
          b64data (-> (get parts 1)
                      (clojure.string/replace #"-" "+")
                      (clojure.string/replace #"_" "/"))
          data    (js/JSON.parse (js/atob b64data))]
      (js->clj data :keywordize-keys true))))


(defn default-option-formatter [option]
  (str
   (if (map? option)
     (:name option)
     option)))


(defn options [options & {:keys [formatter]
                          :or   {formatter default-option-formatter}}]
  (mapv (fn [option]
          (if (map? option)
            (let [{:keys [_ value]} option]
              [:option {:value value} (formatter option)])
            [:option {:value option} option]))
        options))


(defn options-with-default [options & {:keys [formatter]
                                       :or   {formatter default-option-formatter}}]
  (concat [[:option {:value nil} ""]]
          (mapv (fn [option]
                  (if (map? option)
                    (let [{:keys [_ value]} option]
                      [:option {:value value} (formatter option)])
                    [:option {:value option} option]))
                options)))


(defn user-expired? [user-data]
  (let [exp      (get user-data :exp 0)
        date-exp (js/Date. (* exp 1000))
        date-now (js/Date.)]
    (isBefore date-exp date-now)))


(defn date-expired? [date]
  (let [date-exp (js/Date. date)
        date-now (js/Date.)]
    (isBefore date-exp date-now)))


(defn date-before? [date1 date2]
  (let [date-left  (js/Date. date1)
        date-right (js/Date. date2)]
    (isBefore date-left date-right)))


(defn rgb->rgba [color opacity]
  (-> color
      (c.string/replace #"rgb" "rgba")
      (c.string/replace #"\)" (str ", " opacity ")"))))

(defn keep-non-nil-keys
  "Removes all nil keys from a map"
  [m]
  (reduce
   (fn [m' [k v]]
     (if (nil? v)
       m'
       (assoc m' k v)))
   {}
   m))


(defn try-reading-string-with-error [s]
  (try
    {:result (parse-string s)}
    (catch js/Error e
      {:result  nil
       :error   true
       :data    (ex-data e)
       :message (.-message e)})))


(defn try-reading-string [s]
  (try
    (cljs.reader/read-string s)
    (catch js/Error e
      (js/console.error "Failed to read string" s)
      (js/console.error e)
      nil)))


(defn maybe-local-url [url]
  (if (and (some? url)
           (re-matches #"http.*" url))
    url
    (str "/img/" url)))


(defn compare-version-then-updated-at [{ver-a :version upd-a :updated-at :or {ver-a "0.0.0"}}
                                       {ver-b :version upd-b :updated-at :or {ver-b "0.0.0"}}]
  (let [compare-version (app.semver/compare ver-a ver-b)]
    (if (zero? compare-version)
      (compare upd-a upd-b)
      compare-version)))


(defn find-connector-by-type-and-version [connectors type version]
  (let [all-matching (filter (comp #{type} :id) connectors)
        sorted (sort compare-version-then-updated-at all-matching)]
    (if (some? version)
      (last (filter (comp #{version} :version) sorted))
      ;; last one will be the most recent version
      (last sorted))))

(defn get-browser-tz []
  (or (-> js/Intl .DateTimeFormat .resolvedOptions .-timeZone)
      (let [offset (-> (js/Date.) .getTimezoneOffset (/ 60) (* -1))]
        (str "UTC" (if (neg? offset) "-" "+") offset))))

(def short-duration
  (.humanizer humanizeDuration
              (clj->js {:language "shortEn"
                        :languages {"shortEn" {:y #(-> "y")
                                               :mo #(-> "mo")
                                               :w #(-> "w")
                                               :d #(-> "d")
                                               :h #(-> "h")
                                               :m #(-> "m")
                                               :s #(-> "s")
                                               :ms #(-> "ms")}}})))

(defn format-execution-time
  [status]
  (let [finish-date (or (:finished-at status) (:completed-at status))
        start-date (:started-at status)]
    (when-let [execution-time (and start-date finish-date
                                   (differenceInMilliseconds (js/Date. finish-date) (js/Date. start-date)))]
      (short-duration
       execution-time
       #js {:round (> execution-time 1000)}))))

(defn format-transform-status
  [timezone run-status & [status]]
  (let [finish-date     (or (:finished-at run-status) (:completed-at run-status))
        execution-time  (format-execution-time run-status)
        format-last-run #(str " last run at " %)
        last-run        (some-> (:last-run-at run-status) (format-date {:timezone-to timezone}) format-last-run)]
    (cond
      (#{:paused :inactive} (some-> status keyword))
      "Paused"
      (:is-executing run-status)
      "Running..."
      :else
      (case (:status run-status)
        "skipped"                 "Transform skipped"
        "failed"                  (str "Failed " (format-date finish-date {:timezone-to timezone}) last-run)
        ("succeeded" "completed") (str "Finished " (format-date finish-date {:timezone-to timezone}) (when execution-time " in ") execution-time last-run)
        "pending"                 "Pending"
        "Never executed"))))

(defn format-transform-status-js [timezone run-status & [status]]
  (format-transform-status timezone (js->clj run-status :keywordize-keys true) status))


(defn save-buffer-to-file [array-buffer filename]
  (let [blob     (new js/Blob (clj->js [array-buffer]))
        blob-url (.createObjectURL js/window.URL blob)]

    (doto  (js/document.createElement "a")
      (.setAttribute "href" blob-url)
      (.setAttribute "download" filename)
      (.click))

    (js/setTimeout (fn [] (js/window.URL.revokeObjectURL blob-url))
                   0)))

(defn toggle [coll el]
  (into
   []
   (if (some #(= % el) coll)
     (remove #(= % el) coll)
     (conj coll el))))

(defn dispatch-async [[event params]]
  (new js/Promise
       (fn [resolve reject]
         (dispatch [event
                    (-> params
                        (update :on-success (fn [cb]
                                              (when cb (cb))
                                              (resolve)))
                        (update :on-failure (fn [cb]
                                              (when cb (cb))
                                              (reject))))]))))

(defn scroll-into-view [node]
  (if (.-scrollIntoViewIfNeeded node)
    (.scrollIntoViewIfNeeded node #js {:behavior "smooth"})
    (.scrollIntoView node #js {:behavior "smooth"})))

(defn try-parse-features [variant]
  (try
    (-> variant :payload :value js/JSON.parse)
    (catch js/Error _)))

(def ^:private sandbox-dag-prefix "sandbox")

(defn get-dag-id [anchor-id sandbox?]
  (str (if sandbox? sandbox-dag-prefix "pipeline-editor") ":" anchor-id))

(defn sandbox-dag-id?
  "Whether a `get-dag-id` id addresses the sandbox graph rather than the live one.
   The id is the source of truth for which mode a graph was fetched for — more
   reliable than the editor's current mode, since the live graph is also rendered
   outside the editor (the read-only pipeline preview in the extract drawer)."
  [dag-id]
  (c.string/starts-with? (str dag-id) (str sandbox-dag-prefix ":")))
